import { Injectable } from '@angular/core';
import { GetScaleAccountProgress, HistoryEvent } from 'erm-sdk';

export type HistoryStatus = 'info' | 'success' | 'warning' | 'error';

export interface HistoryItem {
  id: number;
  when: Date;
  type: string;
  title: string;
  status: HistoryStatus;
  details?: string;
  raw?: HistoryEvent;
}

@Injectable()
export class ScaleProgressNormalizerService {
  buildHistoryList(progress: GetScaleAccountProgress | null | undefined): HistoryItem[] {
    if (!progress?.progressEvents?.length) {
      return [];
    }

    const items: HistoryItem[] = progress.progressEvents
      .filter(Boolean)
      .map((event) => this.normalizeEvent(event as HistoryEvent))
      .filter((item): item is HistoryItem => !!item);

    // Sort ascending by time (the component can reverse if needed)
    items.sort((a, b) => a.when.getTime() - b.when.getTime());
    return items;
  }

  // ========== Internals ==========

  private normalizeEvent(event: HistoryEvent): HistoryItem | null {
    const record = (event ?? {}) as Record<string, unknown>;

    const type = this.getEventType(record);
    const when = this.safeDate((record as any).timestamp);
    // Keep original id if present; otherwise use timestamp or now
    const id = (record as any).id ?? Number(when?.getTime() ?? Date.now());

    if (!type || !when) {
      return null;
    }

    const name = this.getStateName(record);
    const [title, status, details] = this.describeEvent(type, record, name);

    return { id, when, type, title, status, details, raw: event };
  }

  private getEventType(record: Record<string, unknown>): string | null {
    const anyType = (record as any).type;
    if (!anyType) {
      return null;
    }
    if (typeof anyType === 'string') {
      return anyType;
    }
    if (typeof (anyType as any)?.value === 'string') {
      return (anyType as any).value;
    }
    return null;
  }

  private safeDate(value: unknown): Date | null {
    if (!value) {
      return null;
    }
    try {
      const date = value instanceof Date ? value : new Date(String(value));
      return isNaN(date.getTime()) ? null : date;
    }
    catch {
      return null;
    }
  }

  private getStateName(record: Record<string, unknown>): string | undefined {
    return (
      (record as any).stateEnteredEventDetails?.name ||
      (record as any).stateExitedEventDetails?.name ||
      (record as any).mapStateStartedEventDetails?.name ||
      undefined
    );
  }

  private shortJson(input?: string | object | null): string | undefined {
    if (!input) {
      return undefined;
    }
    try {
      const obj = typeof input === 'string' ? JSON.parse(input) : input;
      const string = JSON.stringify(obj);
      return string.length > 240 ? string.slice(0, 237) + '…' : string;
    }
    catch {
      const string = String(input);
      return string.length > 240 ? string.slice(0, 237) + '…' : string;
    }
  }

  private joinNonEmpty(parts: Array<string | undefined>): string | undefined {
    const joined = parts.filter(Boolean).join(' · ');
    return joined || undefined;
  }

  private statusFromType(type: string): HistoryStatus {
    if (/(failed|timedout|aborted)/i.test(type)) {
      return 'error';
    }
    if (/(succeeded|executionsucceeded)/i.test(type)) {
      return 'success';
    }
    if (/(redriven|schedulefailed|startfailed|submitfailed)/i.test(type)) {
      return 'warning';
    }
    return 'info';
  }

  private fallbackStatus(type: string): HistoryStatus {
    if (/succeed/i.test(type)) {
      return 'success';
    }
    if (/(fail|error|timeout|abort)/i.test(type)) {
      return 'error';
    }
    if (/(redrive|retry)/i.test(type)) {
      return 'warning';
    }
    return 'info';
  }

  private describeEvent(
    type: string,
    record: Record<string, unknown>,
    name?: string
  ): [title: string, status: HistoryStatus, details?: string] {
    const label = name ? `: ${name}` : '';
    const status: HistoryStatus = this.statusFromType(type);

    switch (type) {
      case 'ExecutionStarted': {
        const input = (record as any).executionStartedEventDetails?.input ?? (record as any).stateEnteredEventDetails?.input;
        const roleArn = (record as any).executionStartedEventDetails?.roleArn;
        const details = this.joinNonEmpty([
          input ? `input=${this.shortJson(input)}` : undefined,
          roleArn ? `role=${roleArn}` : undefined,
        ]);
        return ['Execution started', 'info', details];
      }

      case 'ExecutionSucceeded': {
        const output = (record as any).executionSucceededEventDetails?.output;
        return ['Execution completed successfully', 'success', output ? `output=${this.shortJson(output)}` : undefined];
      }

      case 'ExecutionFailed': {
        const details = (record as any).executionFailedEventDetails;
        const message = this.joinNonEmpty([details?.error, details?.cause]);
        return ['Execution failed', 'error', message];
      }

      case 'ExecutionAborted':
        return ['Execution aborted', 'error'];

      case 'ExecutionTimedOut':
        return ['Execution timed out', 'error'];

      case 'ChoiceStateEntered':
        return [`Entered choice state${label}`, 'info'];

      case 'ChoiceStateExited':
        return [`Exited choice state${label}`, 'info'];

      case 'TaskScheduled': {
        const details = (record as any).taskScheduledEventDetails;
        const resource = details?.resource;
        return [`Task scheduled${label}`, 'info', resource ? `resource=${resource}` : undefined];
      }

      case 'TaskStarted':
        return [`Task started${label}`, 'info'];

      case 'TaskSubmitted':
        return [`Task submitted${label}`, 'info'];

      case 'TaskSucceeded': {
        const output = (record as any).taskSucceededEventDetails?.output;
        return [`Task completed${label}`, 'success', output ? `output=${this.shortJson(output)}` : undefined];
      }

      case 'TaskFailed': {
        const details = (record as any).taskFailedEventDetails;
        const message = this.joinNonEmpty([details?.error, details?.cause]);
        return [`Task failed${label}`, 'error', message];
      }

      case 'TaskTimedOut':
        return [`Task timed out${label}`, 'error'];

      case 'TaskStartFailed': {
        const details = (record as any).taskStartFailedEventDetails;
        const message = this.joinNonEmpty([details?.error, details?.cause]);
        return [`Failed to start task${label}`, 'warning', message];
      }

      case 'TaskSubmitFailed': {
        const details = (record as any).taskSubmitFailedEventDetails;
        const message = this.joinNonEmpty([details?.error, details?.cause]);
        return [`Failed to submit task${label}`, 'warning', message];
      }

      case 'StateEntered': {
        const input = (record as any).stateEnteredEventDetails?.input;
        return [`Entered state${label}`, 'info', input ? `input=${this.shortJson(input)}` : undefined];
      }

      case 'StateExited': {
        const output = (record as any).stateExitedEventDetails?.output;
        return [`Exited state${label}`, 'info', output ? `output=${this.shortJson(output)}` : undefined];
      }

      case 'LambdaFunctionScheduled':
        return ['Lambda scheduled', 'info'];

      case 'LambdaFunctionSucceeded': {
        const output = (record as any).lambdaFunctionSucceededEventDetails?.output;
        return ['Lambda completed', 'success', output ? `output=${this.shortJson(output)}` : undefined];
      }

      case 'LambdaFunctionFailed': {
        const details = (record as any).lambdaFunctionFailedEventDetails;
        const message = this.joinNonEmpty([details?.error, details?.cause]);
        return ['Lambda failed', 'error', message];
      }

      case 'LambdaFunctionStartFailed': {
        const details = (record as any).lambdaFunctionStartFailedEventDetails;
        const message = this.joinNonEmpty([details?.error, details?.cause]);
        return ['Failed to start Lambda', 'warning', message];
      }

      case 'LambdaFunctionTimedOut':
        return ['Lambda timed out', 'error'];

      case 'MapStateStarted':
        return [`Map started${label}`, 'info'];

      case 'MapRunStarted':
        return ['MapRun started', 'info'];

      case 'MapRunRedriven':
        return ['MapRun redriven', 'warning'];

      case 'MapRunFailed':
        return ['MapRun failed', 'error'];

      case 'MapIterationStarted':
        return ['Map iteration started', 'info'];

      case 'MapIterationSucceeded':
        return ['Map iteration completed', 'success'];

      case 'MapIterationFailed':
        return ['Map iteration failed', 'error'];

      case 'MapIterationAborted':
        return ['Map iteration aborted', 'error'];

      case 'EvaluationFailed': {
        const details = (record as any).evaluationFailedEventDetails;
        const message = this.joinNonEmpty([details?.error, details?.cause]);
        return ['Evaluation failed', 'error', message];
      }

      default: {
        const guessed = this.fallbackStatus(type);
        return [type, guessed, name ? `state=${name}` : undefined];
      }
    }
  }
}
